/** Autocompletado de ubicación (Colombia) vía Photon — sin clave de Google. */

export type LocationSuggestion = {
  description: string;
  lat: string;
  lon: string;
};

type PhotonFeature = {
  geometry?: { coordinates?: [number, number] };
  properties?: {
    name?: string;
    street?: string;
    housenumber?: string;
    locality?: string;
    district?: string;
    city?: string;
    county?: string;
    state?: string;
    country?: string;
    countrycode?: string;
  };
};

function formatSuggestion(props: NonNullable<PhotonFeature['properties']>): string {
  const parts: string[] = [];
  if (props.street?.trim()) {
    const street = props.street.trim();
    parts.push(props.housenumber?.trim() ? `${street} ${props.housenumber.trim()}` : street);
  } else if (props.name?.trim()) {
    parts.push(props.name.trim());
  }
  const zone =
    props.locality?.trim() ||
    props.district?.trim() ||
    props.county?.trim();
  if (zone && !parts.includes(zone)) {
    parts.push(zone);
  }
  const city = props.city?.trim();
  if (city && !parts.includes(city)) {
    parts.push(city);
  }
  const state = props.state?.trim();
  if (state && !parts.includes(state)) {
    parts.push(state);
  }
  return parts.join(', ');
}

export async function searchLocationSuggestions(query: string): Promise<LocationSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) {
    return [];
  }

  const qs = new URLSearchParams({
    q,
    limit: '10',
    bbox: '-79.0,-4.3,-66.8,13.6',
  });

  try {
    const res = await fetch(`https://photon.komoot.io/api?${qs.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      return searchNominatimSuggestions(q);
    }
    const data = (await res.json()) as { features?: PhotonFeature[] };
    const out: LocationSuggestion[] = [];
    const seen = new Set<string>();
    for (const feature of data.features ?? []) {
      const props = feature.properties ?? {};
      if (props.countrycode && props.countrycode.toUpperCase() !== 'CO') {
        continue;
      }
      const coords = feature.geometry?.coordinates;
      if (!coords || coords.length < 2) {
        continue;
      }
      const lon = coords[0];
      const lat = coords[1];
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        continue;
      }
      const description = formatSuggestion(props);
      if (!description.trim() || seen.has(description)) {
        continue;
      }
      seen.add(description);
      out.push({
        description,
        lat: String(lat),
        lon: String(lon),
      });
      if (out.length >= 7) {
        break;
      }
    }
    if (out.length > 0) {
      return out;
    }
    return searchNominatimSuggestions(q);
  } catch {
    return searchNominatimSuggestions(q);
  }
}

type NominatimSearchRow = {
  lat?: string;
  lon?: string;
  display_name?: string;
};

async function searchNominatimSuggestions(query: string): Promise<LocationSuggestion[]> {
  const qs = new URLSearchParams({
    q: query,
    format: 'jsonv2',
    addressdetails: '1',
    countrycodes: 'co',
    limit: '7',
  });
  try {
    const res = await fetch(`/geocode/search?${qs.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      return [];
    }
    const rows = (await res.json()) as NominatimSearchRow[];
    const out: LocationSuggestion[] = [];
    for (const row of rows) {
      const lat = row.lat?.trim();
      const lon = row.lon?.trim();
      const description = row.display_name?.trim();
      if (!lat || !lon || !description) {
        continue;
      }
      out.push({ description, lat, lon });
    }
    return out;
  } catch {
    return [];
  }
}
